/**
 * Pure helpers of the Assets panel (W2-10 projects-data; DESIGN.md §4.18, §5.5, §7.6; formats P§2.1).
 *
 *  - who uses a file (lines by texture, notes by hit sound, effects by shader, videos, and the chart's song,
 *    illustration and unlock video) and how that reads ("used by 2 lines and 31 notes");
 *  - file kind and role from what the io worker sniffed (or the file name), the accept filter of a role;
 *  - problems Phira has with a file: missing, not decodable by Phira, image larger than 2048 px (no mipmaps:
 *    shimmers when shrunk, PH-27), names Phira mishandles (non-ASCII PH-31, ending in click.png or export.json
 *    PH-05, a hit sound named tap.mp3 / flick.mp3 / drag.mp3 PH-38);
 *  - the song-select preview range after the song changes: Phira refuses to open a chart whose preview ends after
 *    the song (PH-36, with a 0.5 s margin); `previewClamp` proposes a range inside the new song.
 */

import { assetPathProblem } from '../../core/model/invariants';
import type { AssetId, AssetRef, AssetRole, AssetUsage, ChartDoc, ChartMeta, DocIndexes } from '../../core/model/types';
import type { MessageKey } from '../../i18n/types';
import type { IconName } from '../../app/registry/types';
import { m } from './assets.messages';

// ---------------------------------------------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------------------------------------------

export const ROLE_ORDER: readonly AssetRole[] = [
  'music',
  'illustration',
  'texture',
  'gif',
  'hitsound',
  'shader',
  'video',
  'font',
  'other',
];

export const ROLE_TITLES: Readonly<Record<AssetRole, MessageKey>> = {
  music: m.roleMusic,
  illustration: m.roleIllustration,
  texture: m.roleTexture,
  gif: m.roleGif,
  hitsound: m.roleHitsound,
  shader: m.roleShader,
  video: m.roleVideo,
  font: m.roleFont,
  other: m.roleOther,
};

export const ROLE_ICONS: Readonly<Record<AssetRole, IconName>> = {
  music: 'FileMusic',
  illustration: 'FileImage',
  texture: 'Image',
  gif: 'Film',
  hitsound: 'Volume2',
  shader: 'FileCode',
  video: 'FileVideo',
  font: 'FileType',
  other: 'File',
};

/** File picker filter for a role (empty: any file). */
export function acceptFor(role: AssetRole | null): string {
  switch (role) {
    case 'music':
    case 'hitsound':
      return 'audio/*,.ogg,.mp3,.wav,.flac,.opus,.m4a,.aac';
    case 'illustration':
    case 'texture':
      return 'image/*,.png,.jpg,.jpeg,.webp,.bmp,.gif';
    case 'gif':
      return 'image/gif,.gif';
    case 'video':
      return 'video/*,.mp4,.webm,.mov';
    case 'shader':
      return '.glsl,.frag,.fs,.vert,.txt,text/plain';
    case 'font':
      return '.ttf,.otf,.woff,.woff2,font/*';
    case 'other':
    case null:
      return '';
  }
}

export type MediaKind = 'audio' | 'image' | 'video' | 'text' | 'other';

/** What is known about a file's content. */
export interface FileFacts {
  readonly mime: string;
  readonly codec: string;
  readonly kind: MediaKind;
  readonly phiraDecodable: boolean;
}

const EXT_FACTS: Readonly<Record<string, FileFacts>> = {
  ogg: { mime: 'audio/ogg', codec: 'ogg-vorbis', kind: 'audio', phiraDecodable: true },
  mp3: { mime: 'audio/mpeg', codec: 'mp3', kind: 'audio', phiraDecodable: true },
  wav: { mime: 'audio/wav', codec: 'wav', kind: 'audio', phiraDecodable: true },
  flac: { mime: 'audio/flac', codec: 'flac', kind: 'audio', phiraDecodable: true },
  aac: { mime: 'audio/aac', codec: 'aac-adts', kind: 'audio', phiraDecodable: true },
  opus: { mime: 'audio/ogg', codec: 'opus', kind: 'audio', phiraDecodable: false },
  m4a: { mime: 'audio/mp4', codec: 'm4a', kind: 'audio', phiraDecodable: false },
  png: { mime: 'image/png', codec: 'png', kind: 'image', phiraDecodable: true },
  jpg: { mime: 'image/jpeg', codec: 'jpeg', kind: 'image', phiraDecodable: true },
  jpeg: { mime: 'image/jpeg', codec: 'jpeg', kind: 'image', phiraDecodable: true },
  webp: { mime: 'image/webp', codec: 'webp', kind: 'image', phiraDecodable: true },
  bmp: { mime: 'image/bmp', codec: 'bmp', kind: 'image', phiraDecodable: true },
  gif: { mime: 'image/gif', codec: 'gif', kind: 'image', phiraDecodable: true },
  avif: { mime: 'image/avif', codec: 'avif', kind: 'image', phiraDecodable: false },
  mp4: { mime: 'video/mp4', codec: 'mp4', kind: 'video', phiraDecodable: true },
  webm: { mime: 'video/webm', codec: 'webm', kind: 'video', phiraDecodable: false },
  mov: { mime: 'video/quicktime', codec: 'mov', kind: 'video', phiraDecodable: false },
  glsl: { mime: 'text/plain', codec: 'glsl', kind: 'text', phiraDecodable: true },
  frag: { mime: 'text/plain', codec: 'glsl', kind: 'text', phiraDecodable: true },
  fs: { mime: 'text/plain', codec: 'glsl', kind: 'text', phiraDecodable: true },
  vert: { mime: 'text/plain', codec: 'glsl', kind: 'text', phiraDecodable: true },
  txt: { mime: 'text/plain', codec: 'text', kind: 'text', phiraDecodable: true },
  ttf: { mime: 'font/ttf', codec: 'ttf', kind: 'other', phiraDecodable: true },
  otf: { mime: 'font/otf', codec: 'otf', kind: 'other', phiraDecodable: true },
  woff: { mime: 'font/woff', codec: 'woff', kind: 'other', phiraDecodable: false },
  woff2: { mime: 'font/woff2', codec: 'woff2', kind: 'other', phiraDecodable: false },
};

/** Lower-case extension of a file name ('' when none). */
export function extensionOf(name: string): string {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

/** Facts from the file name and the browser's type when the io worker cannot sniff the content. */
export function factsFromName(name: string, browserType: string): FileFacts {
  const known = EXT_FACTS[extensionOf(name)];
  if (known !== undefined) return known;
  const mime = browserType === '' ? 'application/octet-stream' : browserType;
  const kind: MediaKind = mime.startsWith('audio/')
    ? 'audio'
    : mime.startsWith('image/')
      ? 'image'
      : mime.startsWith('video/')
        ? 'video'
        : mime.startsWith('text/')
          ? 'text'
          : 'other';
  return { mime, codec: extensionOf(name), kind, phiraDecodable: false };
}

/** The role a new file gets when the caller does not say (sounds are hit sounds, images textures...). */
export function inferRole(facts: FileFacts, name: string): AssetRole {
  const ext = extensionOf(name);
  if (facts.kind === 'audio') return 'hitsound';
  if (facts.kind === 'image') return facts.codec === 'gif' ? 'gif' : 'texture';
  if (facts.kind === 'video') return 'video';
  if (['glsl', 'frag', 'fs', 'vert'].includes(ext)) return 'shader';
  if (['ttf', 'otf', 'woff', 'woff2'].includes(ext)) return 'font';
  return 'other';
}

/** Whether a file of these facts can serve in `role`. */
export function fitsRole(facts: FileFacts, role: AssetRole): boolean {
  switch (role) {
    case 'music':
    case 'hitsound':
      return facts.kind === 'audio';
    case 'illustration':
    case 'texture':
    case 'gif':
      return facts.kind === 'image';
    case 'video':
      return facts.kind === 'video';
    case 'shader':
      return facts.kind === 'text';
    case 'font':
    case 'other':
      return true;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Usage
// ---------------------------------------------------------------------------------------------------------------

export function usageCount(u: AssetUsage): number {
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length;
}

export interface AssetRow {
  readonly ref: AssetRef;
  readonly usage: AssetUsage;
  readonly uses: number;
}

/** Every file of the chart with its users, in role order then by path. */
export function assetRows(doc: ChartDoc, idx: DocIndexes): AssetRow[] {
  const rows = [...doc.assets.values()].map((ref) => {
    const usage = idx.assetUsers(ref.id);
    return { ref, usage, uses: usageCount(usage) };
  });
  const rank = (r: AssetRole): number => ROLE_ORDER.indexOf(r);
  return rows.sort(
    (a, b) => rank(a.ref.role) - rank(b.ref.role) || (a.ref.path < b.ref.path ? -1 : a.ref.path > b.ref.path ? 1 : 0),
  );
}

export interface UsageText {
  readonly locale: string;
  readonly t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
}

/** "Used by 2 lines and 31 notes", "Song of the chart", "Not used". */
export function usageText(text: UsageText, u: AssetUsage): string {
  const parts: string[] = [];
  if (u.meta.includes('music')) parts.push(text.t(m.usedSong));
  if (u.meta.includes('illustration')) parts.push(text.t(m.usedIllustration));
  if (u.meta.includes('unlockVideo')) parts.push(text.t(m.usedUnlockVideo));
  if (u.lines.length > 0) parts.push(text.t(m.usedLines, { count: u.lines.length }));
  if (u.notes.length > 0) parts.push(text.t(m.usedNotes, { count: u.notes.length }));
  if (u.effects.length > 0) parts.push(text.t(m.usedEffects, { count: u.effects.length }));
  if (u.videos.length > 0) parts.push(text.t(m.usedVideos, { count: u.videos.length }));
  if (parts.length === 0) return text.t(m.unused);
  return text.t(m.usedBy, { users: joinList(text.locale, parts) });
}

/** "a, b and c" in the locale's words. */
export function joinList(locale: string, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Problems
// ---------------------------------------------------------------------------------------------------------------

/** Images larger than this on a side get a downscale offer (PH-27: no mipmaps, shimmers when shrunk). */
export const LARGE_IMAGE_PX = 2048;

/** Downscale choices offered (longest side in px). */
export const DOWNSCALE_SIDES: readonly number[] = [2048, 1350, 1024];

export function isImage(ref: Pick<AssetRef, 'role' | 'mime'>): boolean {
  return ref.mime.startsWith('image/') || ref.role === 'illustration' || ref.role === 'texture' || ref.role === 'gif';
}

/** Longest side of an image (null when unknown). */
export function imageMaxSide(ref: Pick<AssetRef, 'meta'>): number | null {
  const w = ref.meta?.width;
  const h = ref.meta?.height;
  if (w === undefined || h === undefined) return null;
  return Math.max(w, h);
}

/** A still image (not a GIF) larger than LARGE_IMAGE_PX: offered to downscale. */
export function canDownscale(ref: AssetRef): boolean {
  const side = imageMaxSide(ref);
  return (
    ref.missing !== true &&
    isImage(ref) &&
    ref.meta?.codec !== 'gif' &&
    ref.role !== 'gif' &&
    side !== null &&
    side > LARGE_IMAGE_PX
  );
}

export type AssetProblem =
  | { readonly kind: 'missing' }
  | { readonly kind: 'undecodable'; readonly codec: string }
  | { readonly kind: 'large'; readonly width: number; readonly height: number }
  | { readonly kind: 'nonAscii' }
  | { readonly kind: 'badName'; readonly reason: string };

/** What Phira would have trouble with in a file (most severe first). */
export function assetProblems(row: AssetRow): AssetProblem[] {
  const { ref, usage } = row;
  const out: AssetProblem[] = [];
  if (ref.missing === true) out.push({ kind: 'missing' });
  else if (ref.meta?.phiraDecodable === false) out.push({ kind: 'undecodable', codec: ref.meta.codec ?? '' });
  const role: AssetRole = usage.notes.length > 0 ? 'hitsound' : ref.role;
  const pathProblem = assetPathProblem(ref.path, role);
  if (pathProblem !== null) out.push({ kind: 'badName', reason: pathProblem });
  if (/[^\x20-\x7e]/.test(ref.path)) out.push({ kind: 'nonAscii' });
  if (canDownscale(ref)) out.push({ kind: 'large', width: ref.meta?.width ?? 0, height: ref.meta?.height ?? 0 });
  return out;
}

/** Last path segment ("tex/arrow.png" -> "arrow.png"), for downloads. */
export function baseName(path: string): string {
  const i = path.lastIndexOf('/');
  const name = i >= 0 ? path.slice(i + 1) : path;
  return name === '' ? 'file' : name;
}

/** The path a downscaled copy gets: same name, extension of the new encoding. */
export function downscaledPath(path: string, mime: 'image/png' | 'image/jpeg'): string {
  const ext = mime === 'image/png' ? '.png' : '.jpg';
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  const stem = dot > slash + 1 ? path.slice(0, dot) : path;
  return `${stem}${ext}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Song preview range
// ---------------------------------------------------------------------------------------------------------------

/** Phira's load check margin: browser decoders may report padding Phira does not decode. */
export const PREVIEW_MARGIN_SEC = 0.5;

/**
 * A preview range inside a song of `durationSec`, or null when the current one already fits. Phira's load check
 * uses `previewEnd ?? previewStart + 1`; new ranges follow the wizard's rule (start = min(start, max(0, length -
 * 16)), end = start + 15, clamped to length - 0.5).
 */
export function previewClamp(
  meta: Pick<ChartMeta, 'previewStart' | 'previewEnd'>,
  durationSec: number,
): { readonly previewStart: number; readonly previewEnd: number | null } | null {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return null;
  const limit = Math.max(0, durationSec - PREVIEW_MARGIN_SEC);
  const loadEnd = meta.previewEnd ?? meta.previewStart + 1;
  if (loadEnd <= limit && meta.previewStart <= limit) return null;
  let start = Math.min(meta.previewStart, Math.max(0, durationSec - 16));
  let end: number | null = Math.min(start + 15, limit);
  if (end <= start) {
    start = 0;
    end = limit > 0 ? limit : null;
  }
  const round = (x: number): number => Math.round(x * 1000) / 1000;
  return { previewStart: round(start), previewEnd: end === null ? null : round(end) };
}

/** Seconds as "1:05.5". */
export function formatSec(sec: number): string {
  const s = Math.max(0, sec);
  const min = Math.floor(s / 60);
  const rest = s - min * 60;
  const text = (Math.round(rest * 10) / 10).toFixed(1).padStart(4, '0');
  return `${String(min)}:${text}`;
}

/** Id helper for tests and callers holding plain strings. */
export function assetId(id: string): AssetId {
  return id as AssetId;
}
